import { Controller } from "@hotwired/stimulus";

import { useResponsive } from "@bryq/employers-src/behaviors/use_responsive";
import { IS_CYPRESS, IS_SANDBOX } from "@bryq/employers-src/settings";
import { getCustomPropertyValue } from "@bryq/employers-src/utils/css";
import Highcharts from "@bryq/employers-src/utils/highcharts";

export default class extends Controller {
  static targets = ["chartContainer"];

  static values = {
    numberOfHighPerformers: Number,
    numberOfMidPerformers: Number,
    numberOfLowPerformers: Number,
    highPerformersDataSeriesName: String,
    midPerformersDataSeriesName: String,
    lowPerformersDataSeriesName: String,
    chartTitle: String,
    pointFormat: String,
    isLarge: Boolean,
  };

  static classes = ["isLarge"];

  connect() {
    useResponsive(this);
  }

  /**
   * Handle the breakpoint change event.
   * Renders the chart based on the current selection for the large breakpoint.
   * @param {Object} breakpoints
   * @param {string} breakpoints.new
   * @param {string} breakpoints.old
   */
  breakpointChange(breakpoints) {
    const isLarge =
      IS_SANDBOX === true ? this.isLargeValue : breakpoints.new !== "x-small";
    this.renderChart(isLarge);
  }

  // eslint-disable-next-line class-methods-use-this
  renderChart(isLarge) {
    const colorAccentBlue60 = getCustomPropertyValue("--color-accent-blue-60");
    const colorNetural80 = getCustomPropertyValue("--color-neutral-80");
    const colorGraphOption4 = getCustomPropertyValue("--color-graph-option-4");
    const colorGraphOption8 = getCustomPropertyValue("--color-graph-option-8");

    const chartOptions = {
      chart: {
        style: {
          fontFamily: "inherit",
        },
      },
      title: {
        text: this.chartTitleValue,
        align: "left",
        useHTML: true,
        margin: 0,
        x: 10,
      },
      credits: {
        enabled: false,
      },
      legend: {
        symbolRadius: 2,
        labelFormatter() {
          return `<span style="color: ${colorNetural80}"><b>${this.name} (${this.y})</b></span>`;
        },
      },
      plotOptions: {
        series: {
          animation: IS_CYPRESS === false,
          borderWidth: 0,
          borderRadius: 0,
          colorByPoint: true,
          type: "pie",
          size: "100%",
          innerSize: "80%",
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      colors: [colorAccentBlue60, colorGraphOption8, colorGraphOption4],
      tooltip: {
        headerFormat: "",
        pointFormat: this.pointFormatValue,
      },
      series: [
        {
          type: "pie",
          data: [
            {
              name: this.highPerformersDataSeriesNameValue,
              y: this.numberOfHighPerformersValue,
            },
            {
              name: this.midPerformersDataSeriesNameValue,
              y: this.numberOfMidPerformersValue,
            },
            {
              name: this.lowPerformersDataSeriesNameValue,
              y: this.numberOfLowPerformersValue,
            },
          ],
        },
      ],
    };

    if (isLarge) {
      chartOptions.chart.height = 234;
      chartOptions.chart.width = 354;
      chartOptions.legend.align = "right";
      chartOptions.legend.layout = "vertical";
      chartOptions.legend.verticalAlign = "middle";
      this.element.classList.add(this.isLargeClass);
    } else {
      chartOptions.chart.height = 324;
      chartOptions.chart.width = 230;
      this.element.classList.remove(this.isLargeClass);
    }

    Highcharts.chart(this.chartContainerTarget, chartOptions);
  }
}
